(() => {
  if (window.__lgdProductPurchaseItemsLoaded) return;
  window.__lgdProductPurchaseItemsLoaded = true;

  const rootSelector = '[data-product-purchase-items-block]';

  const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const replaceTokens = (template, savePercent, frequency) => String(template || '')
    .replace(/\{save_percent\}/g, String(savePercent || 0))
    .replace(/\{selling_plan_frequency\}/g, String(frequency || ''));

  const sanitiseBenefitMarkup = (value) => String(value || '')
    .replace(/<strong[^>]*>/gi, '<strong>')
    .replace(/<\/strong[^>]*>/gi, '</strong>')
    .replace(/<(?!\/?strong>)[^>]*>/gi, '');

  const renderBenefitText = (element, value) => {
    if (!element) return;

    const parts = sanitiseBenefitMarkup(value).split(/(<strong>|<\/strong>)/gi);
    let strong = null;
    element.replaceChildren();

    parts.forEach((part) => {
      if (/^<strong>$/i.test(part)) {
        strong = document.createElement('strong');
        element.appendChild(strong);
      } else if (/^<\/strong>$/i.test(part)) {
        strong = null;
      } else if (part) {
        (strong || element).appendChild(document.createTextNode(part));
      }
    });
  };

  function initialise(root) {
    if (!root || root.dataset.purchaseItemsInitialised === 'true') return;
    root.dataset.purchaseItemsInitialised = 'true';

    const packs = Array.from(root.querySelectorAll('[data-ppi-pack-option]'));
    const purchaseInputs = Array.from(root.querySelectorAll('[data-purchase-type-input]'));
    const form = root.querySelector('form[data-type="add-to-cart-form"]');
    const variantInput = form?.querySelector('[data-main-variant-id]');
    const sellingPlanInput = form?.querySelector('[data-main-selling-plan]');
    const includedGiftsInput = form?.querySelector('[data-included-gifts]');
    const includedGiftProperties = form?.querySelector('[data-included-gift-properties]');
    const submitButton = form?.querySelector('button[type="submit"]');
    const subscriptionCard = root.querySelector('[data-purchase-type="subscription"]');
    const gifts = Array.from(root.querySelectorAll('[data-product-purchase-gift]'));
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const priceDisplay = root.dataset.priceDisplay || 'total';
    const eachSuffix = root.dataset.eachSuffix || 'each';
    const useManualOnetimeSavings = root.dataset.useManualOnetimeSavings === 'true';
    let lastPackTier = null;
    let lastPurchaseType = null;

    root.querySelectorAll('[data-pack-image]').forEach((image) => {
      const media = image.closest('[data-pack-media]');
      const placeholder = media?.querySelector('[data-pack-placeholder]');
      let fallbackAttempted = false;

      const recoverImage = () => {
        const fallbackSrc = image.dataset.fallbackSrc;
        if (!fallbackAttempted && fallbackSrc && image.src !== fallbackSrc) {
          fallbackAttempted = true;
          image.src = fallbackSrc;
          return;
        }

        image.hidden = true;
        if (placeholder) placeholder.hidden = false;
      };

      image.addEventListener('error', recoverImage);
      if (image.complete && image.naturalWidth === 0) recoverImage();
    });

    const formatMoney = (cents) => {
      const value = Math.round(toNumber(cents));
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(value, moneyFormat);
      }

      const amount = (value / 100).toFixed(2);
      const amountNoDecimals = Math.round(value / 100).toString();
      return moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals);
    };

    const selectedPack = () => packs.find((pack) => pack.querySelector('input[type="radio"]')?.checked)
      || packs.find((pack) => !pack.querySelector('input[type="radio"]')?.disabled)
      || packs[0];

    const purchaseType = () => purchaseInputs.find((input) => input.checked)?.value || 'onetime';

    const setPurchaseType = (type) => {
      const input = purchaseInputs.find((item) => item.value === type && !item.disabled);
      if (input) input.checked = true;
    };

    const priceForDisplay = (total, count) => priceDisplay === 'each'
      ? Math.round(toNumber(total) / Math.max(1, toNumber(count, 1)))
      : toNumber(total);

    const formatDisplayedPrice = (cents) => `${formatMoney(cents)}${priceDisplay === 'each' ? `/${eachSuffix}` : ''}`;

    const manualOnetimeDiscount = (pack) => {
      if (!pack || !useManualOnetimeSavings) return 0;

      const rawDiscount = String(pack.dataset.ppiManualOnetimeDiscount || '0')
        .replace('%', '')
        .trim();
      const discount = Number.parseFloat(rawDiscount);

      if (!Number.isFinite(discount) || discount <= 0) return 0;
      return Math.min(discount, 100);
    };

    const discountedPrice = (price, discountPercent) => Math.round(
      toNumber(price) * (1 - toNumber(discountPercent) / 100)
    );

    const updatePrice = (compareElement, priceElement, compare, current, count) => {
      const displayedCompare = priceForDisplay(compare, count);
      const displayedCurrent = priceForDisplay(current, count);
      if (priceElement) priceElement.textContent = formatDisplayedPrice(displayedCurrent);
      if (!compareElement) return;

      if (displayedCompare > displayedCurrent) {
        compareElement.textContent = formatDisplayedPrice(displayedCompare);
        compareElement.hidden = false;
      } else {
        compareElement.textContent = '';
        compareElement.hidden = true;
      }
    };

    const updateGifts = (pack, type) => {
      const tier = toNumber(pack?.dataset.ppiPackTier, 1);
      const unlockedGiftNames = [];
      const unlockedGifts = [];

      gifts.forEach((gift) => {
        const unlockFrom = type === 'subscription'
          ? gift.dataset.subscriptionUnlockFrom
          : gift.dataset.onetimeUnlockFrom;
        const minimumTier = toNumber(unlockFrom);
        const unlocked = minimumTier > 0 && tier >= minimumTier;
        const status = gift.querySelector('[data-gift-status]');
        const statusText = unlocked
          ? gift.dataset.unlockedLabel || 'Unlocked'
          : gift.dataset.lockedLabel || 'Locked';
        const giftName = String(gift.dataset.giftName || '').trim();
        gift.classList.toggle('is-unlocked', unlocked);
        gift.classList.toggle('is-locked', !unlocked);
        gift.setAttribute('aria-disabled', String(!unlocked));
        gift.setAttribute('aria-label', giftName ? `${giftName}: ${statusText}` : statusText);
        if (status) status.textContent = statusText;
        if (unlocked && giftName) {
          unlockedGiftNames.push(giftName);
          unlockedGifts.push({ name: giftName, value: 'FREE' });
        }
      });

      const hasUnlockedGifts = unlockedGiftNames.length > 0;
      const giftsTitle = root.querySelector('[data-gifts-title]');
      const giftsContainer = root.querySelector('[data-gifts-container]');
      const lockedTitle = root.querySelector('[data-gifts-locked-title]')?.textContent || 'UPGRADE YOUR TIER';
      const unlockedTitle = root.querySelector('[data-gifts-unlocked-title]')?.textContent || 'GIFTS UNLOCKED';
      if (giftsTitle) giftsTitle.textContent = hasUnlockedGifts ? unlockedTitle : lockedTitle;
      giftsContainer?.classList.toggle('is-upgrade', !hasUnlockedGifts);
      if (includedGiftsInput) includedGiftsInput.value = unlockedGiftNames.join(', ');
      if (includedGiftProperties) {
        const fragment = document.createDocumentFragment();
        unlockedGifts.forEach((gift) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${gift.name.replace(/[\[\]]/g, '').trim()}]`;
          input.value = gift.value;
          fragment.appendChild(input);
        });
        includedGiftProperties.replaceChildren(fragment);
      }
    };

    const render = () => {
      const pack = selectedPack();
      if (!pack) return;

      const planId = pack.dataset.ppiSellingPlanId || '';
      let type = purchaseType();
      if (type === 'subscription' && !planId) {
        type = 'onetime';
        setPurchaseType(type);
      }

      packs.forEach((item) => {
        const input = item.querySelector('input[type="radio"]');
        item.classList.toggle('is-selected', Boolean(input?.checked));
        const itemOnetimePrice = toNumber(item.dataset.ppiOnetimePrice);
        const current = type === 'subscription'
          ? item.dataset.ppiSubscriptionPrice
          : discountedPrice(itemOnetimePrice, manualOnetimeDiscount(item));
        const unitPrice = item.querySelector('[data-pack-unit-price]');
        if (unitPrice) unitPrice.textContent = `${formatMoney(toNumber(current) / Math.max(1, toNumber(item.dataset.ppiCount, 1)))} ${eachSuffix}`;
      });

      root.querySelectorAll('[data-purchase-card]').forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.purchaseType === type);
      });
      root.querySelectorAll('[data-subscription-only-content]').forEach((content) => {
        content.hidden = type !== 'subscription';
      });

      const subscriptionInput = purchaseInputs.find((input) => input.value === 'subscription');
      if (subscriptionInput) subscriptionInput.disabled = !planId;
      subscriptionCard?.classList.toggle('is-disabled', !planId);

      const count = toNumber(pack.dataset.ppiCount, 1);
      const original = toNumber(pack.dataset.ppiOriginalPrice);
      const onePrice = toNumber(pack.dataset.ppiOnetimePrice);
      const subPrice = toNumber(pack.dataset.ppiSubscriptionPrice);
      const savePercent = toNumber(pack.dataset.ppiSubscriptionSave);
      const onetimeDiscount = manualOnetimeDiscount(pack);
      const onetimeDisplayPrice = discountedPrice(onePrice, onetimeDiscount);
      const onetimeComparePrice = onetimeDiscount > 0 ? onePrice : original;

      updatePrice(root.querySelector('[data-subscription-compare]'), root.querySelector('[data-subscription-price]'), original, subPrice, count);
      updatePrice(root.querySelector('[data-onetime-compare]'), root.querySelector('[data-onetime-price]'), onetimeComparePrice, onetimeDisplayPrice, count);

      const badge = root.querySelector('[data-subscription-badge]');
      if (badge) badge.textContent = replaceTokens(badge.dataset.template, savePercent, pack.dataset.ppiSellingPlanFrequency);
      root.querySelectorAll('[data-benefit-text]').forEach((benefit) => {
        renderBenefitText(benefit, replaceTokens(benefit.dataset.template, savePercent, pack.dataset.ppiSellingPlanFrequency));
      });

      if (variantInput) variantInput.value = pack.dataset.ppiVariantId || '';
      if (sellingPlanInput) {
        sellingPlanInput.value = planId;
        sellingPlanInput.disabled = type !== 'subscription' || !planId;
      }

      const currentPrice = type === 'subscription' ? subPrice : onetimeDisplayPrice;
      const atcPrice = root.querySelector('[data-atc-price]');
      if (atcPrice) atcPrice.textContent = formatMoney(currentPrice);

      if (submitButton) {
        const unavailable = pack.dataset.ppiAvailable !== 'true' || !pack.dataset.ppiVariantId;
        submitButton.disabled = unavailable;
        submitButton.setAttribute('aria-disabled', String(unavailable));
      }

      updateGifts(pack, type);

      const selectedTier = toNumber(pack.dataset.ppiPackTier, 1);
      if (selectedTier !== lastPackTier) {
        lastPackTier = selectedTier;
        root.dispatchEvent(new CustomEvent('lgd:product-pack-change', {
          bubbles: true,
          detail: { tier: selectedTier, purchaseType: type },
        }));
      }

      if (type !== lastPurchaseType) {
        lastPurchaseType = type;
        root.dispatchEvent(new CustomEvent('lgd:product-purchase-type-change', {
          bubbles: true,
          detail: { tier: selectedTier, purchaseType: type },
        }));
      }
    };

    const initiallySelected = selectedPack();
    if (initiallySelected && initiallySelected.querySelector('input[type="radio"]')?.disabled) {
      const firstAvailable = packs.find((pack) => !pack.querySelector('input[type="radio"]')?.disabled);
      if (firstAvailable) firstAvailable.querySelector('input[type="radio"]').checked = true;
    }

    packs.forEach((pack) => pack.querySelector('input[type="radio"]')?.addEventListener('change', render));
    purchaseInputs.forEach((input) => input.addEventListener('change', render));
    render();
  }

  const initialiseAll = (scope = document) => {
    if (scope.matches?.(rootSelector)) initialise(scope);
    scope.querySelectorAll?.(rootSelector).forEach(initialise);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
